// Sidebar status icon — Lucide iconography, Cursor-parity.
//
//   working (derived, task-scoped)           → Loader (animate-spin)
//   idle (not working)                       → CircleCheck
//   error                                    → CircleAlert (red)
//   archived (section, passed in by parent)  → Archive
//
// Sprint working-signal-ssot (D013-EC-136/137) — the spinner keys off the
// server-derived `working` signal, not `status === 'running'`: a stuck-
// but-alive turn, an open permission card, a background subagent, or a
// pending wakeup all keep it spinning ("has the agent finished its
// task?", Santi 2026-08-09). `error` outranks `working` — a red row must
// never be masked by an optimistic spinner (e.g. an errored agent whose
// pending wakeup will resurrect it). When the caller doesn't pass
// `working` (legacy tests), it is derived from status (running/stuck).
//
// Colour binding: only `error` sets its own colour (status-error red).
// Every other icon inherits `currentColor` from the parent (the agent
// list row) so the icon and the agent name stay locked in lockstep —
// when the row is selected and the name brightens, the icon brightens
// with it. Sprint `sidebar-row-alignment-and-color` (2026-05-12)
// introduced the inherit pattern; previously each branch hardcoded
// `text-muted-foreground` which broke parity with the name on hover /
// selected states.
//
// strokeWidth bumped to 2.25 (default Lucide is 2). The slightly heavier
// stroke compensates for the darker row text colour (AgentListItem's idle
// `text-muted-foreground/75`) so the icon stays legible against the dark
// sidebar background.
//
// DS.5 (sprint aos-ds-shell, spec §2 D3): stays on `atoms/Icon` (Lucide) —
// the four-state semantics are untouched, and the DS `Icon` primitive has no
// attribute passthrough for the `data-testid` / `aria-label` / `stroke-width`
// the frozen tests read on the SVG (AC-10). The skin role reads are the
// Tailwind status classes below (`text-status-*` → the skin's `--text-*`).

import type { AgentStatus } from '../types';
import { Icon } from './atoms/Icon';
import { useTranslation } from '../i18n';

const STROKE_WIDTH = 2.25;

export interface StatusIconProps {
  status: AgentStatus;
  /**
   * Sprint job-tree-four-states (D-028 §2.1) — the four founder states,
   * derived server-side from the pending-job tree. When present it WINS
   * over `status` and `working`: those are lifecycle facts, this is the
   * founder-visible truth, and mixing them is the conflation that made
   * "stopped waiting for you" and "working" render identically.
   *
   * Optional so every existing caller and story keeps its behaviour until
   * it passes the new prop.
   */
  founderState?: 'working' | 'needs-you' | 'done' | 'unconfirmed';
  /** Severity split inside `needs-you` — five icons, four states. */
  severity?: 'normal' | 'unrecoverable';
  /** Server-derived task-scoped signal (Agent.working). Optional so
   * status-only callers keep the pre-sprint mapping. */
  working?: boolean;
  /** When true, overrides `status` and renders the Archive glyph. The
   * parent decides because "archived" is a section, not a status. */
  archived?: boolean;
  /** Icon size in px. Defaults to 13 (matches existing 13 px sidebar
   * iconography — Pin button, hover Archive, hover Trash2). */
  size?: number;
  className?: string;
}

export function StatusIcon({
  status,
  working,
  founderState,
  severity = 'normal',
  archived = false,
  size = 13,
  className = '',
}: StatusIconProps) {
  const { t } = useTranslation();
  const isWorking = working ?? (status === 'running' || status === 'stuck');

  if (archived) {
    return (
      <Icon
        name="Archive"
        size={size}
        strokeWidth={STROKE_WIDTH}
        className={className}
        aria-label={t('status.archived')}
        data-testid="status-icon-archived"
        data-status="archived"
      />
    );
  }

  // ── The four founder states (D-028) ──────────────────────────────────
  // Archived still wins above: it is a section, not a state.
  if (founderState) {
    if (founderState === 'needs-you') {
      // THE state this whole design exists for. Before it, a conversation
      // blocked on Santi looked identical to twenty that were advancing,
      // so the one signal he could act on was the one that disappeared.
      const unrecoverable = severity === 'unrecoverable';
      return (
        <Icon
          name={unrecoverable ? 'CircleAlert' : 'CircleUser'}
          size={size}
          strokeWidth={STROKE_WIDTH}
          // `status-warning`, NOT `status-warn`. The first draft wrote the
          // latter, which is not a token (`tailwind.config.js` defines
          // success / warning / error / info), so Tailwind emitted nothing
          // and THE most important state in the design — the one this
          // whole sprint exists to make visible — rendered in the
          // inherited grey of every other row. A design-discipline miss
          // that a green suite cannot catch: the class name is a string.
          className={`${unrecoverable ? 'text-status-error' : 'text-status-warning'} ${className}`}
          aria-label={t('status.needsYou')}
          data-testid="status-icon-needs-you"
          data-status={unrecoverable ? 'needs-you-unrecoverable' : 'needs-you'}
        />
      );
    }
    if (founderState === 'unconfirmed') {
      // We cannot verify, so we decline to answer rather than guess. The
      // muted glyph is deliberate: it must not read as a failure — it
      // resolves itself when the runner comes back.
      return (
        <Icon
          name="CircleDashed"
          size={size}
          strokeWidth={STROKE_WIDTH}
          className={`text-muted-foreground ${className}`}
          aria-label={t('status.unconfirmed')}
          data-testid="status-icon-unconfirmed"
          data-status="unconfirmed"
        />
      );
    }
    if (founderState === 'working') {
      return (
        <Icon
          name="Loader"
          size={size}
          strokeWidth={STROKE_WIDTH}
          className={`animate-spin ${className}`}
          aria-label={t('status.running')}
          data-testid="status-icon-running"
          data-status={status}
        />
      );
    }
    // `done` — nothing pending. The composer is the resume (D-008).
    return (
      <Icon
        name="CircleCheck"
        size={size}
        strokeWidth={STROKE_WIDTH}
        className={className}
        aria-label={t('status.idle')}
        data-testid="status-icon-idle"
        data-status={status}
      />
    );
  }

  if (status === 'error') {
    return (
      <Icon
        name="CircleAlert"
        size={size}
        strokeWidth={STROKE_WIDTH}
        className={`text-status-error ${className}`}
        aria-label={t('status.error')}
        data-testid="status-icon-error"
        data-status="error"
      />
    );
  }

  if (isWorking) {
    return (
      <Icon
        name="Loader"
        size={size}
        strokeWidth={STROKE_WIDTH}
        className={`animate-spin ${className}`}
        aria-label={t('status.running')}
        data-testid="status-icon-running"
        data-status={status}
      />
    );
  }

  // Everything else collapses to "idle" (D-008 binary). One signal: the
  // agent finished its task; send a message to continue.
  return (
    <Icon
      name="CircleCheck"
      size={size}
      strokeWidth={STROKE_WIDTH}
      className={className}
      aria-label={t('status.idle')}
      data-testid={`status-icon-${status}`}
      data-status={status}
    />
  );
}
